import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const EASE = [0.16, 1, 0.3, 1];

export default function PortraitReveal() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Layered scroll-driven transforms
  const imgScale = useTransform(scrollYProgress, [0, 0.5, 1], [1.25, 1.02, 0.96]);
  const imgY = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const imgBlur = useTransform(scrollYProgress, [0, 0.45, 0.55, 1], [10, 0, 0, 8]);
  const imgFilter = useTransform(imgBlur, (b) => `grayscale(100%) contrast(1.08) blur(${b}px)`);

  // Clip-path reveal (curtain opening top + bottom)
  const clipTop = useTransform(scrollYProgress, [0, 0.5], [25, 0]);
  const clipBottom = useTransform(scrollYProgress, [0, 0.5], [25, 0]);
  const clipPath = useTransform(
    [clipTop, clipBottom],
    ([t, b]) => `inset(${t}% 0% ${b}% 0%)`
  );

  // Vignette
  const overlayOpacity = useTransform(scrollYProgress, [0, 0.35, 0.65, 1], [0.95, 0.25, 0.3, 0.9]);

  // Side ticker text — slides horizontally as you scroll
  const tickerX = useTransform(scrollYProgress, [0, 1], ["20%", "-30%"]);

  // Editorial heading
  const headingY = useTransform(scrollYProgress, [0.15, 0.55], [80, 0]);
  const headingOpacity = useTransform(scrollYProgress, [0.15, 0.4, 0.85, 1], [0, 1, 1, 0.2]);

  // Bottom caption
  const captionOpacity = useTransform(scrollYProgress, [0.3, 0.55, 0.85, 1], [0, 1, 1, 0.3]);

  return (
    <section
      ref={ref}
      id="founder"
      data-testid="portrait-section"
      className="relative h-screen w-screen overflow-hidden bg-[#0A0A0A]"
    >
      {/* Layer 1: Image with curtain clip + parallax + blur defocus on enter/exit */}
      <motion.div
        style={{ scale: imgScale, y: imgY, clipPath, WebkitClipPath: clipPath }}
        className="absolute inset-0 will-change-transform"
      >
        <motion.img
          src="/jay-portrait.jpg"
          alt="JAY — founder, JAY-INK Academy"
          draggable={false}
          style={{ filter: imgFilter }}
          className="w-full h-full object-cover select-none"
        />
      </motion.div>

      {/* Layer 2: gradient vignette */}
      <motion.div
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/85 via-[#0A0A0A]/10 to-[#0A0A0A]/95 pointer-events-none"
      />

      {/* Layer 3: huge horizontal ticker word that slides across */}
      <motion.div
        style={{ x: tickerX }}
        className="absolute top-1/2 -translate-y-1/2 left-0 w-[180%] pointer-events-none whitespace-nowrap z-10 flex items-center gap-16"
      >
        {[...Array(2)].map((_, i) => (
          <span
            key={i}
            className="font-display uppercase text-white/10 select-none"
            style={{
              fontSize: "clamp(8rem, 22vw, 22rem)",
              letterSpacing: "0.06em",
              fontWeight: 200,
              lineHeight: 1,
            }}
          >
            FOUNDER&nbsp;·&nbsp;JAY-INK&nbsp;·
          </span>
        ))}
      </motion.div>

      {/* Top labels */}
      <div className="absolute top-24 md:top-28 left-6 md:left-12 right-6 md:right-12 flex justify-between items-center text-white/70 z-20">
        <span className="text-[10px] md:text-xs tracking-[0.4em] uppercase">— Founder / 001</span>
        <span className="hidden md:inline text-[10px] tracking-[0.4em] uppercase">Sydney, AU</span>
      </div>

      {/* Centered editorial heading */}
      <motion.div
        style={{ y: headingY, opacity: headingOpacity }}
        className="absolute inset-0 z-20 flex flex-col items-center justify-center px-6 text-center"
      >
        <h2
          className="font-display text-white uppercase leading-none"
          style={{
            fontSize: "clamp(3.5rem, 14vw, 14rem)",
            letterSpacing: "0.04em",
            fontWeight: 200,
            mixBlendMode: "screen",
          }}
        >
          {"JAY".split("").map((ch, i) => (
            <motion.span
              key={i}
              initial={{ y: "100%", opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true, margin: "-20%" }}
              transition={{ duration: 1, ease: EASE, delay: 0.15 * i }}
              className="inline-block"
            >
              {ch}
            </motion.span>
          ))}
        </h2>
      </motion.div>

      {/* Bottom caption */}
      <motion.div
        style={{ opacity: captionOpacity }}
        className="absolute bottom-16 md:bottom-20 left-6 md:left-12 right-6 md:right-12 z-20"
      >
        <p className="max-w-md text-white/85 text-sm md:text-base font-light leading-relaxed">
          Sydney&rsquo;s home to the best Micro Realism &amp; Fine Line Artists…
        </p>
      </motion.div>

      {/* Bottom-right corner mark */}
      <div className="absolute bottom-6 left-6 md:left-12 text-white/45 text-[10px] tracking-[0.3em] uppercase z-20">
        JAY-INK / FOUNDER
      </div>
    </section>
  );
}
